import { useState } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { Mic } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useIvrEditor } from '../../context';
import type { IvrNodeData, RecordAction } from '../../types';
import { NodeCard } from '../NodeCard';

export function RecordNode(props: NodeProps) {
  const { updateNodeData, onRemove, prompts } = useIvrEditor();
  const data = props.data as IvrNodeData;
  const action = (data.action ?? {}) as RecordAction;
  const recordDuration = data.record_duration ?? '';

  const [open, setOpen] = useState(false);

  const welcomePrompts = prompts.filter(
    (p) =>
      p.promptCategory === 'welcome' &&
      (p.promptStatus === undefined ||
        Number(p.promptStatus) === 1 ||
        p.promptStatus === '1'),
  );

  const onPick = (promptIdStr: string) => {
    const picked = welcomePrompts.find(
      (p) => String(p.promptId) === promptIdStr,
    );
    if (!picked) return;
    updateNodeData(props.id, {
      action: {
        type: 'record',
        id: picked.promptId,
        audio: (picked as { promptUrl?: string }).promptUrl ?? '',
        duration: (picked as { promptDuration?: number }).promptDuration ?? 0,
        name: picked.fileName,
      } as RecordAction,
    });
  };

  return (
    <>
      <Handle
        type="target"
        position={Position.Left}
        className="!h-3 !w-3 !border-2 !border-white !bg-rose-500"
      />
      <NodeCard
        verb="record"
        icon={Mic}
        label="Record"
        status={action.audio && recordDuration ? 'configured' : 'incomplete'}
        selected={props.selected}
        summary={
          action.name ? (
            <span className="truncate">
              <span className="font-mono">{action.name}</span>
              {recordDuration && (
                <span className="text-muted-foreground"> · up to {recordDuration}s</span>
              )}
            </span>
          ) : (
            <span className="text-muted-foreground">Pick prompt + duration</span>
          )
        }
        onConfigure={() => setOpen(true)}
        onRemove={() => onRemove(props.id)}
      />
      <Handle
        type="source"
        position={Position.Right}
        className="!h-3 !w-3 !border-2 !border-white !bg-rose-500"
      />

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Record caller</SheetTitle>
            <SheetDescription>
              Play a prompt, then record the caller&apos;s response for up to the
              configured duration.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4 space-y-3">
            <div className="space-y-2">
              <Label>
                Prompt audio
                <span className="ml-1 text-destructive">*</span>
              </Label>
              <Select value={String(action.id ?? '')} onValueChange={onPick}>
                <SelectTrigger>
                  <SelectValue placeholder="Pick a prompt" />
                </SelectTrigger>
                <SelectContent>
                  {welcomePrompts.map((p) => (
                    <SelectItem key={p.promptId} value={String(p.promptId)}>
                      {p.fileName ?? p.promptName ?? `#${p.promptId}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>
                Max recording (seconds)
                <span className="ml-1 text-destructive">*</span>
              </Label>
              <Input
                inputMode="numeric"
                value={String(recordDuration)}
                onChange={(e) =>
                  updateNodeData(props.id, {
                    record_duration: e.target.value.replace(/\D/g, ''),
                  })
                }
                placeholder="e.g. 30"
              />
            </div>
          </div>
          <SheetFooter>
            <Button onClick={() => setOpen(false)}>Done</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}
